import { skipToken, useQuery } from "@tanstack/react-query";
import {
  getTransactionsPublicID,
  type TransactionDetailDto,
} from "Infrastructure/Api/Api";
import {
  isRetryableApiError,
  processResponseWithApiProblemDetails,
} from "Infrastructure/Api/ApiUtils";
import { transactionsDetailKeys } from "Api/Queries/Transactions/useTransactionsDetail";
import {
  terminalStatuses,
  POLL_INTERVAL_MS,
} from "Components/Chat/transactionStatusUtils";
import { MS_TIME } from "Utils/TimeUtils";

/**
 * A failing poll is retried inside the same fetch after 5s, 10s, 20s and 40s. When the last retry
 * fails too, the query settles as errored and the interval stops — the next window focus or
 * remount refetches and, on success, resumes normal polling. Keeping the backoff in React Query's
 * retryer (rather than counting failed polls) needs no state of our own: the retry counter is per
 * fetch, unmounting the card cancels pending retries, and Sentry sees one error per streak.
 */
const MAX_POLL_RETRIES = 4;
const FAILURE_BACKOFF_BASE_MS = 5 * MS_TIME.second;

// TODO: Should be replaced with signal-R in the future, out of scope for ZS-1312
export const useTransactionPolling = (publicID?: string | null) =>
  useQuery<TransactionDetailDto>({
    queryKey: transactionsDetailKeys.byPublicId(publicID),
    queryFn: publicID
      ? processResponseWithApiProblemDetails(() =>
          getTransactionsPublicID(publicID),
        )
      : skipToken,
    // Same retryable set as the query client's default (5xx, network); a 4xx will not heal by
    // polling, so it settles at once and stops the interval below.
    retry: (failureCount, error) =>
      isRetryableApiError(error) && failureCount < MAX_POLL_RETRIES,
    retryDelay: (failureCount) => FAILURE_BACKOFF_BASE_MS * 2 ** failureCount,
    refetchInterval: (query) => {
      const { status, data } = query.state;
      const transactionStatus = data?.transactionStatus;
      if (transactionStatus && terminalStatuses.includes(transactionStatus)) {
        return false;
      }

      if (status === "error") {
        return false;
      }

      return POLL_INTERVAL_MS;
    },
  });
